import React from 'react'
import {
  Close as CloseIcon,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from '@mui/icons-material'
import { CircularProgress, IconButton, Skeleton } from '@mui/material'
import { makeStyles } from '@mui/styles'
import classNames from 'classnames'
import { is } from 'ramda'

import {
  Check as CheckIcon,
  Decline as DeclineIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Duplicate as DuplicateIcon,
  Edit as EditIcon,
  Email as EmailIcon,
  Estimates as EstimatesIcon,
  Eye as EyeIcon,
  ForwardArrow as ForwardArrowIcon,
  Info as InfoIcon,
  Print as PrintIcon,
  RoundedCheck,
} from '../../icons'
import { ClassesType, Nil } from '../../types'
import PuiTooltip from '../PuiTooltip'
import VerticalDivider from '../VerticalDivider'

const useStyles = makeStyles(
  theme => ({
    editIcon: {
      color: theme.colors.editIconColor,
    },
    checkIcon: {
      color: theme.colors.editIconColor,
    },
    infoIcon: {},
    iconButton: {
      padding: theme.spacing(0.5),
      margin: theme.spacing(0.5),
    },
    declineIcon: {},
    removeIcon: {},
    collapseIcon: {},
    previewIcon: {},
    duplicateIcon: {},
    downloadIcon: {},
    deleteIcon: {},
    emailIcon: {},
    expandIcon: {},
    printIcon: {},
    roundedCheckIcon: {},
    estimatesIcon: {},
    navigateIcon: {},
    info: {
      top: 0,
      left: 0,
    },
    spinner: {
      color: theme.colors.disabledLabelText,
      marginRight: theme.spacing(0.5),
    },
  }),
  { name: 'ControlButtonGroup' },
)

export enum ControlButtonGroupName {
  PREVIEW = 'preview',
  DUPLICATE = 'duplicate',
  DOWNLOAD = 'download',
  REMOVE = 'remove',
  DELETE = 'delete',
  DECLINE = 'decline',
  EDIT = 'edit',
  EMAIL = 'email',
  EXPAND = 'expand',
  COLLAPSE = 'collapse',
  INFO = 'info',
  PRINT = 'print',
  CHECK = 'check',
  ROUNDED_CHECK = 'roundedCheck',
  ESTIMATES = 'estimates',
  NAVIGATE = 'navigate',
}

const ButtonsIconMap = {
  [ControlButtonGroupName.PREVIEW]: EyeIcon,
  [ControlButtonGroupName.DUPLICATE]: DuplicateIcon,
  [ControlButtonGroupName.DOWNLOAD]: DownloadIcon,
  [ControlButtonGroupName.REMOVE]: CloseIcon,
  [ControlButtonGroupName.DELETE]: DeleteIcon,
  [ControlButtonGroupName.DECLINE]: DeclineIcon,
  [ControlButtonGroupName.EDIT]: EditIcon,
  [ControlButtonGroupName.EMAIL]: EmailIcon,
  [ControlButtonGroupName.EXPAND]: KeyboardArrowUp,
  [ControlButtonGroupName.COLLAPSE]: KeyboardArrowDown,
  [ControlButtonGroupName.INFO]: InfoIcon,
  [ControlButtonGroupName.PRINT]: PrintIcon,
  [ControlButtonGroupName.CHECK]: CheckIcon,
  [ControlButtonGroupName.ROUNDED_CHECK]: RoundedCheck,
  [ControlButtonGroupName.ESTIMATES]: EstimatesIcon,
  [ControlButtonGroupName.NAVIGATE]: ForwardArrowIcon,
}

export type ControlButtonGroupItem = {
  readonly IconProps?: any
  readonly disabled?: boolean
  readonly isLoading?: boolean
  readonly name: ControlButtonGroupName
  readonly onClick?: (event: React.MouseEvent) => void
  readonly renderer?: (button: JSX.Element) => React.ReactNode
  readonly tooltip?: React.ReactNode
}

export interface ControlButtonGroupProps {
  readonly buttons: (ControlButtonGroupItem | boolean | Nil)[]
  readonly classes?: ClassesType<typeof useStyles>
  readonly disableDivider?: boolean
  isLoading?: boolean
}

const ControlButtonGroup = ({
  isLoading: isLoadingGroup,
  buttons,
  classes: classesProp,
  disableDivider,
}: ControlButtonGroupProps) => {
  const classes = useStyles({ classes: classesProp })

  const validButtons: ControlButtonGroupItem[] = buttons.filter(is(Object))
  const lastIndex = validButtons.length - 1

  return (
    <>
      {validButtons.map(
        (
          {
            name,
            onClick,
            isLoading,
            renderer,
            tooltip,
            disabled,
            IconProps,
          }: ControlButtonGroupItem,
          index,
        ) => {
          const Icon = ButtonsIconMap[name]
          const iconButton = Icon && (
            <IconButton
              aria-label={name}
              className={classNames(classes.iconButton, classes[`${name}Icon`])}
              disabled={disabled || isLoading}
              size="large"
              onClick={onClick}
            >
              {isLoadingGroup ? (
                <Skeleton height={24} variant="circular" width={24} />
              ) : isLoading ? (
                <CircularProgress className={classes.spinner} size={16} />
              ) : (
                <Icon {...IconProps} />
              )}
            </IconButton>
          )

          const button = tooltip ? (
            <PuiTooltip tooltipPlacement="top" tooltipText={tooltip}>
              {iconButton}
            </PuiTooltip>
          ) : (
            iconButton
          )

          return (
            <React.Fragment key={name}>
              {renderer ? renderer(button) : button}
              {index !== lastIndex && !disableDivider && <VerticalDivider />}
            </React.Fragment>
          )
        },
      )}
    </>
  )
}

export default ControlButtonGroup
